<template>
  <div class="w-full flex flex-wrap">

    <div class="md:w-2/3 w-full">

      <div class="mx-auto md:mt-16 w-full flex justify-center">
        <AppLogo class="rounded-2xl h-36 w-[80%]"  />
      </div>
      <Head :title="__('Login to Onsho24')" />

      <form
        @submit.prevent="attempt"
        class="p-8 max-w-[30rem] mx-auto"
      >
        <h2 class="text-2xl text-center font-normal mb-2">
          {{ __('Login to Onsho24') }}
        </h2>

        <div class="mb-6">
          <label class="block mb-2" for="email">
            {{ __('Email') }}
          </label>
          <input
            v-model="form.email"
            class="form-control form-input form-input-bordered w-full"
            :class="{ 'form-input-border-error': form.errors.has('email') }"
            id="email"
            type="email"
            name="email"
            dir="ltr"
            placeholder="example@gmail.com"
            autofocus=""
            required
          />

          <HelpText class="mt-2 text-red-500" v-if="form.errors.has('email')">
            {{ form.errors.first('email') }}
          </HelpText>
        </div>

        <div class="mb-6">
          <label class="block mb-2" for="password">{{ __('Password') }}</label>
          <div class="relative">
            <input
              v-model="form.password"
              class="form-control form-input form-input-bordered w-full pr-10"
              :class="{ 'form-input-border-error': form.errors.has('password') }"
              id="password"
              dir="ltr"
              :type="showPassword ? 'text' : 'password'"
              name="password"
              placeholder="رمز عبور خود را وارد کنید"
              required
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <HeroiconsSolidEye v-if="!showPassword" class="w-5 h-5" />
              <HeroiconsSolidEyeOff v-else class="w-5 h-5" />
            </button>
          </div>

          <HelpText class="mt-2 text-red-500" v-if="form.errors.has('password')">
{{ form.errors.first('password') }}
          </HelpText>
        </div>

        <div class="flex mb-6 items-center justify-between">
          <Checkbox
            @change="() => (form.remember = !form.remember)"
            :model-value="form.remember"
            dusk="remember-button"
            :label="__('Remember me')"
          />

          <div
            v-if="supportsPasswordReset || forgotPasswordPath !== false"
            class="ml-auto"
          >
            <Link
              v-if="forgotPasswordPath === false"
              :href="$url('/password/reset')"
              class="text-primary-500 font-bold underline hover:text-primary-600"
              v-text="__('Forgot your password?')"
            />
            <a
              v-else
              :href="forgotPasswordPath"
              class="text-primary-500 font-bold underline hover:text-primary-600"
              v-text="__('Forgot your password?')"
            />
          </div>
        </div>

        <Button
          class="w-full flex justify-center rounded-lg bg-primary-500 hover:bg-primary-600 text-white"
          type="submit"
          :loading="form.processing"
        >
          <span>
            {{ __('Login with Email') }}
          </span>
        </Button>

        <div class="flex items-center my-6">
          <div class="flex-1 border-t border-[#E9E9E9]"></div>
          <span class="px-4 text-gray-500">{{ __('or') }}</span>
          <div class="flex-1 border-t border-[#E9E9E9]"></div>
        </div>

        <button
          type="button"
          @click="loginWithGoogle"
          class="w-full flex items-center justify-center gap-3 px-4 py-3 border border-[#E9E9E9] rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium transition-colors"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {{ __('Login with Google') }}
        </button>

        <div class="mt-6 text-center">
          <span class="text-gray-600">{{ __('Haven\'t registered?') }}</span>
          <Link
            :href="$url('/tenant/register')"
            class="text-primary-500 font-bold underline hover:text-primary-600 ml-1"
          >
            {{ __('Start 15 days free trial') }}
          </Link>
        </div>

      </form>
    </div>

    <div class="md:w-1/3 md:block hidden h-screen">
      <img src="/images/login-banner.png" alt="Login Image" class="w-full h-screen object-contain">
    </div>



  </div>
</template>

<script>
import Auth from '@/layouts/Auth'
import { Button, Checkbox } from 'laravel-nova-ui'
import { Link, Head } from '@inertiajs/inertia-vue3'
import HeroiconsSolidEye from '@/components/Heroicons/solid/HeroiconsSolidEye'
import HeroiconsSolidEyeOff from '@/components/Heroicons/solid/HeroiconsSolidEyeOff'

export default {
  name: 'TenantLoginPage',

  layout: Auth,

  components: {
    Checkbox,
    Button,
    Link,
    Head,
    HeroiconsSolidEye,
    HeroiconsSolidEyeOff,
  },

  data: () => ({
    showPassword: false,
    form: Nova.form({
      email: '',
      password: '',
      remember: false,
    }),
  }),

  mounted() {
    // نمایش flash messages از session
    if (this.$page.props.flash?.error) {
      Nova.error(this.$page.props.flash.error)
    }
    if (this.$page.props.flash?.success) {
      Nova.success(this.$page.props.flash.success)
    }
  },

  methods: {
    async attempt() {
      try {
        const { redirect } = await this.form.post(Nova.url('/tenant/login'))

        let path = { url: Nova.url('/'), remote: true }

        if (redirect !== undefined && redirect !== null) {
          path = { url: redirect, remote: true }
        }

        Nova.visit(path)
      } catch (error) {
        if (error.response?.status === 500) {
          Nova.error(this.__('There was a problem submitting the form.'))
        }
      }
    },

    loginWithGoogle() {
      // Redirect to Google OAuth endpoint for tenant
      window.location.href = '/auth/google/tenant'
    },
  },

  computed: {
    supportsPasswordReset() {
      return Nova.config('withPasswordReset')
    },

    forgotPasswordPath() {
      return Nova.config('forgotPasswordPath')
    },
  },
}
</script>
